/**
 * 동일값 / 연동 셀(linked-cells) popover 로직 훅.
 *
 * 같은 metadata identity 를 가진 셀들의 위치를 floating panel / popover 로 보여주고,
 * 선택 셀 변화에 따라 panel 상태를 갱신한다. autoSum mismatch(sum-validation) 분기도 포함한다.
 */

import { useCallback, useMemo, useState } from "react";
import { clearFocusedCell } from "@/utils/ixd-dsd-compare/focus-utils";
import {
  getRelatedCellIdentityLocations,
  type CellIdentityLocation,
} from "@/utils/ixd-cell-model";
import {
  type CellLinkageItem,
  type ModifiedCellDisplayValues,
} from "./cell-linkage-floating-panel";
import { type CellPopoverState } from "./preview/cell-linkage-popover";
import {
  attrEqualsSelector,
  exactCellIdSelector,
} from "@/utils/ixd-dsd-compare/cell-id-selector";
import { ATTR } from "@/utils/dom-attrs";

interface UseIxdSameValuePopoverOptions {
  containerRef: React.RefObject<HTMLDivElement | null>;
  focusedCellRef: React.RefObject<HTMLElement | null>;
  selectCellRef: React.RefObject<
    ((uniqueId: string, options?: { scroll?: boolean }) => boolean) | null
  >;
  identityLocationsByKey?: Map<string, CellIdentityLocation[]>;
  /** 값이 들어가지 않은 셀. 연동도 수정도 없을 때 popover 를 여는 판별자를 겸한다 */
  cellNotices?: ReadonlyMap<string, unknown>;
  onFocusIdentityLocation?: (location: CellIdentityLocation) => boolean | void;
  /**
   * 사용자가 직접 수정한 셀이면 기존/수정 표시값을 반환. 연동이 없는 수정 셀도
   * `modified` 모드로 popover 를 노출하기 위한 판별자를 겸한다.
   */
  getModifiedValues?: (cell: HTMLElement) => ModifiedCellDisplayValues | null;
}

export interface FocusIdentityLocationOptions {
  scrollBehavior?: ScrollBehavior;
  /** false면 화면을 움직이지 않고 선택만 한다 — 사용자가 이미 보고 있는 셀일 때 */
  scroll?: boolean;
}

export function useIxdSameValuePopover({
  containerRef,
  focusedCellRef,
  selectCellRef,
  identityLocationsByKey,
  cellNotices,
  onFocusIdentityLocation,
  getModifiedValues,
}: UseIxdSameValuePopoverOptions) {
  const [cellPopover, setCellPopover] = useState<CellPopoverState | null>(null);

  /**
   * Radix Popover virtualRef — cellPopover.element 의 위치를 anchor 로 사용.
   * 같은 cell 이 유지되는 동안 ref 객체를 안정적으로 보관해 Radix 가 불필요하게
   * 재측정하지 않도록 한다.
   */
  const cellPopoverAnchorRef = useMemo<{ current: HTMLElement | null }>(
    () => ({ current: cellPopover?.element ?? null }),
    [cellPopover?.element],
  );

  const activateSameValueForElement = useCallback(
    (cell: HTMLElement) => {
      if (!containerRef.current || !containerRef.current.contains(cell)) {
        return false;
      }
      const uid = cell.id;
      const identityKey = cell?.getAttribute("data-cell-identity-key");
      return !!(uid && identityKey);
    },
    [containerRef],
  );

  const updateCellPopoverForElement = useCallback(
    (cell: HTMLElement) => {
      // 1) autoSum mismatch 가 최우선 — sum-validation 모드
      if (cell.getAttribute("data-auto-sum-value-different") === "true") {
        const inputValue = cell.getAttribute("data-display-value") ?? "";
        const calculatedValue =
          cell.getAttribute("data-expected-auto-sum-value-display") ?? "";
        const identityKey = cell.getAttribute("data-cell-identity-key");
        setCellPopover((previous) =>
          previous?.mode === "sum-validation" &&
          previous.element === cell &&
          previous.inputValue === inputValue &&
          previous.calculatedValue === calculatedValue &&
          previous.identityKey === identityKey
            ? previous
            : {
                mode: "sum-validation",
                element: cell,
                inputValue,
                calculatedValue,
                identityKey,
              },
        );
        return;
      }

      // 2) 셀에 확인할 알림이 있으면 cell-notice 모드 — 연동·수정보다 먼저다.
      //    패널은 모드와 무관하게 연동 위치와 수정 전후 값을 함께 보여주므로,
      //    모드는 머리 칩(확인 필요)만 정한다.
      {
        const identityKey = cell.getAttribute("data-cell-identity-key");
        if (identityKey && cellNotices?.has(identityKey)) {
          setCellPopover((previous) =>
            previous?.mode === "cell-notice" &&
            previous.element === cell &&
            previous.identityKey === identityKey
              ? previous
              : { mode: "cell-notice", element: cell, identityKey },
          );
          return;
        }
      }

      // 3) 연동된 셀이 있으면 linked-cells 모드로 셀 옆 패널 노출.
      //    보기 모드와 편집 모드(IXD-DSD 대사·IXD 에디터) 모두 동일하게 노출한다.
      {
        const identityKey = cell.getAttribute("data-cell-identity-key");
        if (identityKey && identityLocationsByKey) {
          const currentLocationRef = {
            uid:
              cell.getAttribute("data-cell-identity-location-uid") ??
              (cell.id || undefined),
            normalizedCellId: cell.getAttribute("data-cell-id") ?? undefined,
          };
          const otherLocations =
            getRelatedCellIdentityLocations(
              identityKey,
              identityLocationsByKey,
              currentLocationRef,
            ) ?? [];
          if (otherLocations.length > 0) {
            setCellPopover((previous) =>
              previous?.mode === "linked-cells" &&
              previous.element === cell &&
              previous.identityKey === identityKey
                ? previous
                : { mode: "linked-cells", element: cell, identityKey },
            );
            return;
          }
        }
      }

      // 4) 연동은 없지만 사용자가 직접 수정한 셀 — modified 모드
      if (getModifiedValues?.(cell)) {
        setCellPopover((previous) =>
          previous?.mode === "modified" && previous.element === cell
            ? previous
            : { mode: "modified", element: cell },
        );
        return;
      }

      // 5) 그 외 — 닫기
      setCellPopover((previous) => (previous ? null : previous));
    },
    [cellNotices, identityLocationsByKey, getModifiedValues],
  );

  const focusIdentityLocationInCurrentRenderer = useCallback(
    (
      location: CellIdentityLocation,
      options?: FocusIdentityLocationOptions,
    ): boolean => {
      if (!containerRef.current) return false;
      const uidCell = location.uid
        ? (containerRef.current.querySelector(
            `[data-cell-identity-location-uid="${CSS.escape(location.uid)}"],#${CSS.escape(location.uid)}`,
          ) as HTMLElement | null)
        : null;
      // UID가 있는 위치는 보고서 내 한 셀을 정확히 가리킨다. 이 renderer에 없을 때
      // normalizedCellId fallback을 허용하면 다른 기간의 동명이 셀을 잘못 선택한다.
      if (location.uid && !uidCell) return false;
      const cell =
        uidCell ??
        (() => {
          // 정규화 id 는 `data-cell-id` 에서만 찾는다(`data-source-cell-id` 는 정규화
          // 안 된 값이라 `exactCellIdSelector` 를 그대로 못 쓴다). escape 규칙은
          // `cell-id-selector` 한 곳에 둔다.
          const selectors = [
            attrEqualsSelector(ATTR.cellId, location.normalizedCellId),
          ];
          if (location.sourceCellId) {
            selectors.push(exactCellIdSelector(location.sourceCellId));
          }
          return containerRef.current?.querySelector(
            selectors.join(","),
          ) as HTMLElement | null;
        })();
      if (!cell?.id) return false;
      clearFocusedCell(focusedCellRef);
      if (options?.scrollBehavior && options.scroll !== false) {
        cell.scrollIntoView({
          behavior: options.scrollBehavior,
          block: "center",
        });
      }
      if (
        !selectCellRef.current?.(cell.id, {
          scroll: options?.scroll !== false && !options?.scrollBehavior,
        })
      ) {
        return false;
      }
      return true;
    },
    [containerRef, focusedCellRef, selectCellRef],
  );

  /**
   * autoSum 패널 출처 위치 — 분류 / 표시 포맷팅은 패널 내부가 담당하므로
   * 여기서는 평면 리스트만 만들어 넘긴다.
   *
   * 현재 선택된 셀은 `getRelatedCellIdentityLocations` 의 currentLocation 인자로
   * 제외해 자기 자신은 리스트에 노출하지 않는다. 항목 클릭 시 대응 위치로
   * 포커싱하고, 성공 시 패널을 닫는다.
   */
  const popoverItems = useMemo<CellLinkageItem[]>(() => {
    if (
      !cellPopover ||
      cellPopover.mode === "modified" ||
      !cellPopover.identityKey ||
      !identityLocationsByKey
    ) {
      return [];
    }
    const currentLocationRef = {
      uid:
        cellPopover.element.getAttribute("data-cell-identity-location-uid") ??
        (cellPopover.element.id || undefined),
      normalizedCellId:
        cellPopover.element.getAttribute("data-cell-id") ?? undefined,
    };
    const locations =
      getRelatedCellIdentityLocations(
        cellPopover.identityKey,
        identityLocationsByKey,
        currentLocationRef,
      ) ?? [];
    const focusLocation =
      onFocusIdentityLocation ?? focusIdentityLocationInCurrentRenderer;
    return locations.map((location) => ({
      chunkTitle: location.chunkTitle,
      dlViewTitle: location.dlViewTitle,
      onClick: () => {
        if (focusLocation(location)) {
          setCellPopover(null);
        }
      },
    }));
  }, [
    cellPopover,
    identityLocationsByKey,
    onFocusIdentityLocation,
    focusIdentityLocationInCurrentRenderer,
  ]);

  return {
    cellPopover,
    setCellPopover,
    cellPopoverAnchorRef,
    activateSameValueForElement,
    updateCellPopoverForElement,
    focusIdentityLocationInCurrentRenderer,
    popoverItems,
  };
}
